'use client';

import { useRouter } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

import { useIsFavorite } from '@/hooks/useIsFavorite';
import { useShareInventory } from '@/hooks/useShareInventory';
import { faHeart as faHeartOutline } from '@fortawesome/free-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { faArrowLeft, faShareFromSquare, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IInventory, PropsWithTags } from '@lamesarv-sdk/types';

interface BaseActionButtonProps extends PropsWithTags {
  className?: string;
  onClick: () => void;
  icon: IconDefinition;
  iconClassName?: string;
}

const BaseActionButton = ({ onClick, className, icon, iconClassName, ...props }: BaseActionButtonProps) => (
  <button
    type="button"
    className={twMerge(
      'bg-white rounded-full p-2 lg:p-2.5 flex justify-center items-center hover:bg-black-50',
      className,
    )}
    data-tag-group={props.tagGroup}
    data-tag={props.tagItem}
    onClick={() => {
      onClick();
    }}
  >
    <FontAwesomeIcon
      icon={icon}
      className={twMerge('size-4 md:size-5 text-black-500 pointer-events-none', iconClassName)}
    />
  </button>
);

const InventoryItemShare = ({ item }: { item: IInventory }) => {
  const { canShare, handleShare } = useShareInventory(item);

  if (!canShare) return null;

  return <BaseActionButton icon={faShareFromSquare} onClick={handleShare} />;
};

const FavoriteButton = ({ item }: { item: IInventory }) => {
  const { isFavorite, toggleFavorite } = useIsFavorite(item);

  return (
    <BaseActionButton
      icon={isFavorite ? faHeartSolid : faHeartOutline}
      onClick={toggleFavorite}
      iconClassName={isFavorite ? 'text-red-500' : 'group-hover/favorites:text-red-500'}
      className="group/favorites"
      tagGroup="inventory-item"
      tagItem="inventory-item-favorite"
    />
  );
};

const BackButton = () => {
  const router = useRouter();

  return (
    <div>
      <BaseActionButton icon={faArrowLeft} onClick={() => router.back()} className="lg:hidden" />
    </div>
  );
};

interface ImageActionsProps {
  item: IInventory;
}

export const ImageActions = ({ item }: ImageActionsProps) => (
  <div className="absolute top-0 left-0 w-full p-4 lg:p-5 flex items-center justify-between z-10">
    <BackButton />
    <div className="flex items-center gap-2.5 md:gap-3">
      <InventoryItemShare item={item} />
      <FavoriteButton item={item} />
    </div>
  </div>
);
